import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Box, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { FullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { triggerFileDownload } from '~/infra/utils';
import { invoiceModel } from '~/models';
import styles from '../AdHocInvoiceApproval.module.scss';
import { InvoiceStatusScreen } from '../InvoiceStatusScreen/InvoiceStatusScreen';
import { TryAnchorBanner } from '../TryAnchorBanner/TryAnchorBanner';
import { deriveDisplayState, resolveInvoiceId } from './AdHocStatusView.utils';
import { VerificationBranch } from './VerificationBranch';
export const GetAdHocStatusViewTID = (parentTestId)=>getTestIds(parentTestId);
export const AdHocStatusView = (param)=>{
    let { state, invoice, relationship, isLoading, showFullPageInvoice, onDismissFullPageInvoice, onShowFullPageInvoice, onResolved, renderApproveForm, testId } = param;
    const { AdHocStatusViewTestIds } = useMemo(()=>GetAdHocStatusViewTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { api } = useStoreActions();
    const overlayContext = useOverlayContext();
    const invoiceId = resolveInvoiceId(state, invoice);
    const relationshipId = invoice != null ? invoiceModel.relationshipId(invoice) : '';
    const processingAction = invoice != null ? invoiceModel.processingAction(invoice) : null;
    const downloadInvoicePdf = useCallback(async ()=>{
        if (!invoiceId) return;
        try {
            const url = await api.invoice.downloadPDF(invoiceId);
            await triggerFileDownload({
                url,
                filename: invoiceId
            });
        } catch (unused) {
            overlayContext.showErrorToast(t.common.toasts.anUnknownErrorHasOccurred());
        }
    }, [
        invoiceId,
        overlayContext
    ]);
    const displayState = deriveDisplayState({
        state,
        scoutInvoice: invoice,
        relationship,
        isLoading,
        showFullPageInvoice
    });
    if (displayState.kind === 'approveForm') return /*#__PURE__*/ _jsx(_Fragment, {
        children: renderApproveForm()
    });
    if (displayState.kind === 'loading') {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocStatusViewTestIds.Root(),
            children: /*#__PURE__*/ _jsx(Box, {
                testId: AdHocStatusViewTestIds.LoadingSkeleton(),
                className: styles.skeleton
            })
        });
    }
    if (displayState.kind === 'fullPageInvoice') {
        return /*#__PURE__*/ _jsx(FullPageInvoice, {
            invoiceId: invoiceId,
            relationshipId: relationshipId,
            dismissModal: onDismissFullPageInvoice,
            hideDropdownActions: true
        });
    }
    if (displayState.kind === 'confirmation') {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocStatusViewTestIds.Root(),
            children: /*#__PURE__*/ _jsx(InvoiceStatusScreen, {
                testId: AdHocStatusViewTestIds.InvoiceStatusScreen(),
                title: t.adHocInvoiceApproval.confirmation.title(),
                subtitle: t.adHocInvoiceApproval.confirmation.subtitle({
                    amount: displayState.amount.display(),
                    vendorName: displayState.vendorName
                }),
                description: t.adHocInvoiceApproval.confirmation.note(),
                footer: /*#__PURE__*/ _jsx(TryAnchorBanner, {}),
                animate: displayState.animate
            })
        });
    }
    const viewInvoiceAction = {
        label: t.adHocInvoiceApproval.viewInvoice(),
        onClick: onShowFullPageInvoice,
        biName: 'ad_hoc_invoice_status_view_invoice'
    };
    if (displayState.kind === 'verificationRequired') {
        return /*#__PURE__*/ _jsx(VerificationBranch, {
            testId: AdHocStatusViewTestIds.Root(),
            statusScreenTestId: AdHocStatusViewTestIds.InvoiceStatusScreen(),
            invoiceId: invoiceId,
            stripeUrl: processingAction === null || processingAction === void 0 ? void 0 : processingAction.stripeUrl,
            clientSecret: processingAction === null || processingAction === void 0 ? void 0 : processingAction.clientSecret,
            amount: displayState.amount,
            vendorName: displayState.vendorName,
            viewInvoiceAction: viewInvoiceAction,
            onResolved: onResolved
        });
    }
    const pdfNotReady = invoice != null && !invoice.pdfExists;
    const downloadPdfAction = {
        label: t.common.buttons.downloadPdf(),
        onClick: downloadInvoicePdf,
        biName: 'ad_hoc_invoice_status_download_pdf',
        disabled: pdfNotReady,
        tooltipTitle: pdfNotReady ? t.invoice.generatingPdf() : undefined
    };
    if (displayState.kind === 'paid') {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocStatusViewTestIds.Root(),
            children: /*#__PURE__*/ _jsx(InvoiceStatusScreen, {
                testId: AdHocStatusViewTestIds.InvoiceStatusScreen(),
                title: t.adHocInvoiceApproval.alreadyPaid.title(),
                subtitle: t.adHocInvoiceApproval.alreadyPaid.subtitle({
                    amount: displayState.amount.display(),
                    vendorName: displayState.vendorName
                }),
                description: t.adHocInvoiceApproval.alreadyPaid.description(),
                primaryAction: viewInvoiceAction,
                secondaryAction: downloadPdfAction
            })
        });
    }
    return /*#__PURE__*/ _jsx(Box, {
        testId: AdHocStatusViewTestIds.Root(),
        children: /*#__PURE__*/ _jsx(InvoiceStatusScreen, {
            testId: AdHocStatusViewTestIds.InvoiceStatusScreen(),
            title: t.adHocInvoiceApproval.voided.title(),
            subtitle: t.adHocInvoiceApproval.voided.subtitle({
                amount: displayState.amount.display(),
                vendorName: displayState.vendorName
            }),
            primaryAction: viewInvoiceAction,
            secondaryAction: downloadPdfAction
        })
    });
};
